import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserIcon, LogOutIcon, CheckCircleIcon, XCircleIcon, ClockIcon, EditIcon, SaveIcon, SendIcon, GlobeIcon, GithubIcon, TwitterIcon, LinkedinIcon, MapPinIcon, PlusIcon, TrashIcon, LinkIcon, AwardIcon, FolderIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { TextArea } from '../../components/ui/TextArea';
interface Skill {
  name: string;
  certificationUrl?: string;
}
interface Project {
  title: string;
  url: string;
  description?: string;
}
interface DeveloperProfile {
  name: string;
  title: string;
  bio: string;
  skills: Skill[];
  projects: Project[];
  openTo: string[];
  location: string;
  country: string;
  rateMin: number;
  rateMax: number;
  rateCurrency: string;
  website: string;
  github: string;
  twitter: string;
  linkedin: string;
  avatar: string;
  status: 'draft' | 'pending' | 'approved' | 'rejected';
  availability: 'available' | 'unavailable';
  rejectionReason?: string;
}
const DeveloperDashboard = () => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState<DeveloperProfile | null>(null);
  const [formData, setFormData] = useState<DeveloperProfile>({
    name: '',
    title: '',
    bio: '',
    skills: [],
    projects: [],
    openTo: [],
    location: '',
    country: '',
    rateMin: 50,
    rateMax: 100,
    rateCurrency: 'USD',
    website: '',
    github: '',
    twitter: '',
    linkedin: '',
    avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
    status: 'draft',
    availability: 'available'
  });
  const [newSkill, setNewSkill] = useState({
    name: '',
    certificationUrl: ''
  });
  const [newProject, setNewProject] = useState({
    title: '',
    url: '',
    description: ''
  });
  const [avatarUploadMethod, setAvatarUploadMethod] = useState<'upload' | 'url'>('upload');
  useEffect(() => {
    // Check if user is authenticated
    const isAuth = localStorage.getItem('developerAuth');
    if (!isAuth) {
      navigate('/developer/login');
      return;
    }
    // Load profile from localStorage
    const savedProfile = localStorage.getItem('developerProfile');
    if (savedProfile) {
      const parsedProfile = JSON.parse(savedProfile);
      setProfile(parsedProfile);
      setFormData(parsedProfile);
    } else {
      // New user - set name from signup
      const name = localStorage.getItem('developerName') || '';
      setFormData(prev => ({
        ...prev,
        name
      }));
      setIsEditing(true);
    }
  }, [navigate]);
  const handleLogout = () => {
    localStorage.removeItem('developerAuth');
    localStorage.removeItem('developerEmail');
    localStorage.removeItem('developerName');
    navigate('/developer/login');
  };
  const handleSaveDraft = () => {
    const updatedProfile = {
      ...formData,
      status: 'draft' as const
    };
    localStorage.setItem('developerProfile', JSON.stringify(updatedProfile));
    setProfile(updatedProfile);
    setIsEditing(false);
  };
  const handleSubmitForApproval = () => {
    if (!formData.name || !formData.title || !formData.bio) {
      alert('Please fill in all required fields (Name, Title, Bio)');
      return;
    }
    if (formData.skills.length === 0) {
      alert('Please add at least one skill with certification');
      return;
    }
    const updatedProfile = {
      ...formData,
      status: 'pending' as const
    };
    localStorage.setItem('developerProfile', JSON.stringify(updatedProfile));
    setProfile(updatedProfile);
    setIsEditing(false);
  };
  const handleToggleAvailability = () => {
    if (!profile) return;
    const newAvailability = profile.availability === 'available' ? 'unavailable' : 'available';
    const updatedProfile = {
      ...profile,
      availability: newAvailability
    };
    localStorage.setItem('developerProfile', JSON.stringify(updatedProfile));
    setProfile(updatedProfile);
    setFormData(updatedProfile);
  };
  const handleAddSkill = () => {
    if (newSkill.name.trim() && !formData.skills.some(s => s.name === newSkill.name.trim())) {
      setFormData({
        ...formData,
        skills: [...formData.skills, {
          name: newSkill.name.trim(),
          certificationUrl: newSkill.certificationUrl.trim() || undefined
        }]
      });
      setNewSkill({
        name: '',
        certificationUrl: ''
      });
    }
  };
  const handleRemoveSkill = (skillName: string) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter(s => s.name !== skillName)
    });
  };
  const handleAddProject = () => {
    if (newProject.title.trim() && newProject.url.trim()) {
      setFormData({
        ...formData,
        projects: [...formData.projects, {
          title: newProject.title.trim(),
          url: newProject.url.trim(),
          description: newProject.description.trim() || undefined
        }]
      });
      setNewProject({
        title: '',
        url: '',
        description: ''
      });
    }
  };
  const handleRemoveProject = (index: number) => {
    setFormData({
      ...formData,
      projects: formData.projects.filter((_, i) => i !== index)
    });
  };
  const handleOpenToChange = (option: string) => {
    if (formData.openTo.includes(option)) {
      setFormData({
        ...formData,
        openTo: formData.openTo.filter(o => o !== option)
      });
    } else {
      setFormData({
        ...formData,
        openTo: [...formData.openTo, option]
      });
    }
  };
  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      // Check file type
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file');
        return;
      }
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          avatar: reader.result as string
        });
      };
      reader.readAsDataURL(file);
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return <span className="px-3 py-1 bg-gray-100 text-gray-800 text-sm font-medium rounded-full" data-id="element-344">
            Draft
          </span>;
      case 'pending':
        return <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full flex items-center" data-id="element-345">
            <ClockIcon className="h-4 w-4 mr-1" data-id="element-346" />
            Pending Review
          </span>;
      case 'approved':
        return <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full flex items-center" data-id="element-347">
            <CheckCircleIcon className="h-4 w-4 mr-1" data-id="element-348" />
            Approved
          </span>;
      case 'rejected':
        return <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full flex items-center" data-id="element-349">
            <XCircleIcon className="h-4 w-4 mr-1" data-id="element-350" />
            Rejected
          </span>;
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-351">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm" data-id="element-352">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-353">
          <div className="flex justify-between items-center py-4" data-id="element-354">
            <div className="flex items-center" data-id="element-355">
              <div className="w-10 h-10 bg-[#d73b3e] rounded-full flex items-center justify-center mr-3" data-id="element-356">
                <UserIcon className="h-6 w-6 text-white" data-id="element-357" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-358">
                Developer Dashboard
              </h1>
            </div>
            <Button variant="ghost" onClick={handleLogout} className="flex items-center" data-id="element-359">
              <LogOutIcon className="h-5 w-5 mr-2" data-id="element-360" />
              Logout
            </Button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-361">
        {/* Status Banner */}
        {profile && <div className="mb-8" data-id="element-362">
            <Card className="bg-white" data-id="element-363">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-364">
                <div className="flex items-center" data-id="element-365">
                  <div className="mr-4" data-id="element-366">
                    <img src={profile.avatar} alt={profile.name} className="w-16 h-16 rounded-full object-cover border-2 border-[#FF3B30]" data-id="element-367" />
                  </div>
                  <div data-id="element-368">
                    <h2 className="text-xl font-bold text-gray-900" data-id="element-369">
                      {profile.name}
                    </h2>
                    <p className="text-sm text-gray-600" data-id="element-370">
                      {profile.title || 'No title set'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-371">
                  {getStatusBadge(profile.status)}
                  {profile.status === 'approved' && <Button variant={profile.availability === 'available' ? 'primary' : 'outline'} size="sm" onClick={handleToggleAvailability} data-id="element-372">
                      {profile.availability === 'available' ? 'Available' : 'Unavailable'}
                    </Button>}
                </div>
              </div>
            </Card>
          </div>}
        {/* Rejection Message */}
        {profile?.status === 'rejected' && profile.rejectionReason && <div className="mb-8" data-id="element-373">
            <Card className="bg-red-50 border-red-200" data-id="element-374">
              <div className="flex items-start" data-id="element-375">
                <XCircleIcon className="h-5 w-5 text-red-600 mr-3 mt-0.5" data-id="element-376" />
                <div data-id="element-377">
                  <h3 className="text-sm font-semibold text-red-900 mb-1" data-id="element-378">
                    Profile Rejected
                  </h3>
                  <p className="text-sm text-red-700" data-id="element-379">
                    {profile.rejectionReason}
                  </p>
                  <p className="text-sm text-red-600 mt-2" data-id="element-380">
                    Please update your profile and resubmit for approval.
                  </p>
                </div>
              </div>
            </Card>
          </div>}
        {/* Pending Message */}
        {profile?.status === 'pending' && <div className="mb-8" data-id="element-381">
            <Card className="bg-yellow-50 border-yellow-200" data-id="element-382">
              <div className="flex items-start" data-id="element-383">
                <ClockIcon className="h-5 w-5 text-yellow-600 mr-3 mt-0.5" data-id="element-384" />
                <div data-id="element-385">
                  <h3 className="text-sm font-semibold text-yellow-900 mb-1" data-id="element-386">
                    Profile Under Review
                  </h3>
                  <p className="text-sm text-yellow-700" data-id="element-387">
                    Your profile has been submitted and is currently being
                    reviewed by our team. You will be notified once the review
                    is complete.
                  </p>
                </div>
              </div>
            </Card>
          </div>}
        {/* Profile Form */}
        <Card className="bg-white" data-id="element-388">
          <div className="flex justify-between items-center mb-6" data-id="element-389">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-390">
              Profile Information
            </h3>
            {profile && !isEditing && profile.status !== 'pending' && <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} className="flex items-center" data-id="element-391">
                <EditIcon className="h-4 w-4 mr-1" data-id="element-392" />
                Edit Profile
              </Button>}
          </div>
          {isEditing ? <div className="space-y-6" data-id="element-393">
              {/* Avatar */}
              <div data-id="element-394">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-395">
                  Profile Picture <span className="text-[#FF3B30]" data-id="element-396">*</span>
                </label>
                <div className="flex items-start gap-4" data-id="element-397">
                  <img src={formData.avatar} alt="Avatar preview" className="w-20 h-20 rounded-full object-cover border-2 border-gray-200" data-id="element-398" />
                  <div className="flex-1" data-id="element-399">
                    {/* Toggle between upload and URL */}
                    <div className="flex gap-2 mb-3" data-id="element-400">
                      <button type="button" onClick={() => setAvatarUploadMethod('upload')} className={`px-3 py-1 text-sm rounded ${avatarUploadMethod === 'upload' ? 'bg-[#d73b3e] text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-401">
                        Upload File
                      </button>
                      <button type="button" onClick={() => setAvatarUploadMethod('url')} className={`px-3 py-1 text-sm rounded ${avatarUploadMethod === 'url' ? 'bg-[#d73b3e] text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-402">
                        Enter URL
                      </button>
                    </div>
                    {avatarUploadMethod === 'upload' ? <div data-id="element-403">
                        <input type="file" accept="image/*" onChange={handleAvatarUpload} className="block w-full text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded file:border-0
                            file:text-sm file:font-medium
                            file:bg-[#d73b3e] file:text-white
                            hover:file:bg-[#c02f32]
                            cursor-pointer" data-id="element-404" />
                        <p className="text-xs text-gray-500 mt-1" data-id="element-405">
                          Upload an image (max 5MB, JPG, PNG, GIF)
                        </p>
                      </div> : <div data-id="element-406">
                        <Input value={formData.avatar} onChange={e => setFormData({
                    ...formData,
                    avatar: e.target.value
                  })} placeholder="Enter image URL (e.g., https://example.com/avatar.jpg)" data-id="element-407" />
                        <p className="text-xs text-gray-500 mt-1" data-id="element-408">
                          Enter a direct link to your profile picture
                        </p>
                      </div>}
                  </div>
                </div>
              </div>
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-409">
                <Input label="Full Name" required value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} placeholder="John Doe" data-id="element-410" />
                <Input label="Professional Title" required value={formData.title} onChange={e => setFormData({
              ...formData,
              title: e.target.value
            })} placeholder="Senior Laravel Developer" data-id="element-411" />
              </div>
              <TextArea label="Bio" required value={formData.bio} onChange={e => setFormData({
            ...formData,
            bio: e.target.value
          })} placeholder="Tell us about your experience and expertise..." rows={4} data-id="element-412" />
              {/* Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-413">
                <Input label="City/Location" value={formData.location} onChange={e => setFormData({
              ...formData,
              location: e.target.value
            })} placeholder="San Francisco" data-id="element-414" />
                <Input label="Country" value={formData.country} onChange={e => setFormData({
              ...formData,
              country: e.target.value
            })} placeholder="United States" data-id="element-415" />
              </div>
              {/* Rate */}
              <div data-id="element-416">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-417">
                  Hourly Rate Range
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-418">
                  <Input type="number" value={formData.rateMin} onChange={e => setFormData({
                ...formData,
                rateMin: Number(e.target.value)
              })} placeholder="50" min="0" data-id="element-419" />
                  <Input type="number" value={formData.rateMax} onChange={e => setFormData({
                ...formData,
                rateMax: Number(e.target.value)
              })} placeholder="100" min="0" data-id="element-420" />
                  <select value={formData.rateCurrency} onChange={e => setFormData({
                ...formData,
                rateCurrency: e.target.value
              })} className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF3B30] focus:border-[#FF3B30]" data-id="element-421">
                    <option value="USD" data-id="element-422">USD</option>
                    <option value="EUR" data-id="element-423">EUR</option>
                    <option value="GBP" data-id="element-424">GBP</option>
                  </select>
                </div>
              </div>
              {/* Skills with Certifications */}
              <div data-id="element-425">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-426">
                  Skills & Certifications{' '}
                  <span className="text-[#FF3B30]" data-id="element-427">*</span>
                </label>
                <div className="space-y-3 mb-3" data-id="element-428">
                  <Input value={newSkill.name} onChange={e => setNewSkill({
                ...newSkill,
                name: e.target.value
              })} placeholder="Skill name (e.g., Laravel, PHP, Vue.js)" data-id="element-429" />
                  <div className="flex gap-2" data-id="element-430">
                    <Input value={newSkill.certificationUrl} onChange={e => setNewSkill({
                  ...newSkill,
                  certificationUrl: e.target.value
                })} placeholder="Certification URL (optional)" className="flex-1" data-id="element-431" />
                    <Button type="button" variant="primary" onClick={handleAddSkill} data-id="element-432">
                      <PlusIcon className="h-4 w-4 mr-1" data-id="element-433" />
                      Add
                    </Button>
                  </div>
                </div>
                <div className="space-y-2" data-id="element-434">
                  {formData.skills.map(skill => <div key={skill.name} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg" data-id="element-435">
                      <div className="flex items-center gap-2 flex-1" data-id="element-436">
                        <span className="font-medium text-blue-900" data-id="element-437">
                          {skill.name}
                        </span>
                        {skill.certificationUrl && <a href={skill.certificationUrl} target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:text-blue-800 text-sm" data-id="element-438">
                            <AwardIcon className="h-3 w-3 mr-1" data-id="element-439" />
                            Certificate
                          </a>}
                      </div>
                      <button type="button" onClick={() => handleRemoveSkill(skill.name)} className="text-red-500 hover:text-red-700" data-id="element-440">
                        <TrashIcon className="h-4 w-4" data-id="element-441" />
                      </button>
                    </div>)}
                </div>
              </div>
              {/* Projects */}
              <div data-id="element-442">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-443">
                  Projects
                </label>
                <div className="space-y-3 mb-3" data-id="element-444">
                  <Input value={newProject.title} onChange={e => setNewProject({
                ...newProject,
                title: e.target.value
              })} placeholder="Project title" data-id="element-445" />
                  <Input value={newProject.url} onChange={e => setNewProject({
                ...newProject,
                url: e.target.value
              })} placeholder="Project URL" data-id="element-446" />
                  <div className="flex gap-2" data-id="element-447">
                    <Input value={newProject.description} onChange={e => setNewProject({
                  ...newProject,
                  description: e.target.value
                })} placeholder="Brief description (optional)" className="flex-1" data-id="element-448" />
                    <Button type="button" variant="primary" onClick={handleAddProject} data-id="element-449">
                      <PlusIcon className="h-4 w-4 mr-1" data-id="element-450" />
                      Add
                    </Button>
                  </div>
                </div>
                <div className="space-y-2" data-id="element-451">
                  {formData.projects.map((project, index) => <div key={index} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg" data-id="element-452">
                      <div className="flex-1" data-id="element-453">
                        <div className="flex items-center gap-2" data-id="element-454">
                          <FolderIcon className="h-4 w-4 text-gray-400" data-id="element-455" />
                          <span className="font-medium text-gray-900" data-id="element-456">
                            {project.title}
                          </span>
                        </div>
                        <a href={project.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center mt-1" data-id="element-457">
                          <LinkIcon className="h-3 w-3 mr-1" data-id="element-458" />
                          {project.url}
                        </a>
                        {project.description && <p className="text-sm text-gray-600 mt-1" data-id="element-459">
                            {project.description}
                          </p>}
                      </div>
                      <button type="button" onClick={() => handleRemoveProject(index)} className="text-red-500 hover:text-red-700 ml-2" data-id="element-460">
                        <TrashIcon className="h-4 w-4" data-id="element-461" />
                      </button>
                    </div>)}
                </div>
              </div>
              {/* Open To */}
              <div data-id="element-462">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-463">
                  Open To
                </label>
                <div className="flex flex-wrap gap-3" data-id="element-464">
                  {['Full-time', 'Part-time', 'Contract', 'Freelance', 'Remote'].map(option => <label key={option} className="flex items-center cursor-pointer" data-id="element-465">
                      <input type="checkbox" checked={formData.openTo.includes(option)} onChange={() => handleOpenToChange(option)} className="h-4 w-4 text-[#FF3B30] focus:ring-[#FF3B30] border-gray-300 rounded" data-id="element-466" />
                      <span className="ml-2 text-sm text-gray-700" data-id="element-467">
                        {option}
                      </span>
                    </label>)}
                </div>
              </div>
              {/* Links */}
              <div data-id="element-468">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-469">
                  Social Links
                </label>
                <div className="space-y-3" data-id="element-470">
                  <div className="flex items-center" data-id="element-471">
                    <GlobeIcon className="h-5 w-5 text-gray-400 mr-3" data-id="element-472" />
                    <Input value={formData.website} onChange={e => setFormData({
                  ...formData,
                  website: e.target.value
                })} placeholder="https://yourwebsite.com" className="flex-1" data-id="element-473" />
                  </div>
                  <div className="flex items-center" data-id="element-474">
                    <GithubIcon className="h-5 w-5 text-gray-400 mr-3" data-id="element-475" />
                    <Input value={formData.github} onChange={e => setFormData({
                  ...formData,
                  github: e.target.value
                })} placeholder="https://github.com/yourusername" className="flex-1" data-id="element-476" />
                  </div>
                  <div className="flex items-center" data-id="element-477">
                    <TwitterIcon className="h-5 w-5 text-gray-400 mr-3" data-id="element-478" />
                    <Input value={formData.twitter} onChange={e => setFormData({
                  ...formData,
                  twitter: e.target.value
                })} placeholder="https://twitter.com/yourusername" className="flex-1" data-id="element-479" />
                  </div>
                  <div className="flex items-center" data-id="element-480">
                    <LinkedinIcon className="h-5 w-5 text-gray-400 mr-3" data-id="element-481" />
                    <Input value={formData.linkedin} onChange={e => setFormData({
                  ...formData,
                  linkedin: e.target.value
                })} placeholder="https://linkedin.com/in/yourusername" className="flex-1" data-id="element-482" />
                  </div>
                </div>
              </div>
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200" data-id="element-483">
                <Button variant="outline" onClick={handleSaveDraft} className="flex items-center justify-center" data-id="element-484">
                  <SaveIcon className="h-4 w-4 mr-2" data-id="element-485" />
                  Save as Draft
                </Button>
                <Button variant="primary" onClick={handleSubmitForApproval} className="flex items-center justify-center" data-id="element-486">
                  <SendIcon className="h-4 w-4 mr-2" data-id="element-487" />
                  Submit for Approval
                </Button>
                {profile && <Button variant="ghost" onClick={() => {
              setFormData(profile);
              setIsEditing(false);
            }} data-id="element-488">
                    Cancel
                  </Button>}
              </div>
            </div> : profile ? <div className="space-y-6" data-id="element-489">
              {/* View Mode */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-490">
                <div data-id="element-491">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-492">
                    Name
                  </label>
                  <p className="text-gray-900" data-id="element-493">{profile.name}</p>
                </div>
                <div data-id="element-494">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-495">
                    Title
                  </label>
                  <p className="text-gray-900" data-id="element-496">{profile.title}</p>
                </div>
              </div>
              <div data-id="element-497">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-498">
                  Bio
                </label>
                <p className="text-gray-900" data-id="element-499">{profile.bio}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-500">
                <div data-id="element-501">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-502">
                    Location
                  </label>
                  <p className="text-gray-900 flex items-center" data-id="element-503">
                    <MapPinIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-504" />
                    {profile.location}, {profile.country}
                  </p>
                </div>
                <div data-id="element-505">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-506">
                    Rate
                  </label>
                  <p className="text-gray-900" data-id="element-507">
                    {profile.rateCurrency} ${profile.rateMin}-${profile.rateMax}
                    /hour
                  </p>
                </div>
              </div>
              <div data-id="element-508">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-509">
                  Skills & Certifications
                </label>
                <div className="space-y-2" data-id="element-510">
                  {profile.skills.map(skill => <div key={skill.name} className="flex items-center gap-2 p-2 bg-blue-50 rounded" data-id="element-511">
                      <span className="font-medium text-blue-900" data-id="element-512">
                        {skill.name}
                      </span>
                      {skill.certificationUrl && <a href={skill.certificationUrl} target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:text-blue-800 text-sm" data-id="element-513">
                          <AwardIcon className="h-3 w-3 mr-1" data-id="element-514" />
                          View Certificate
                        </a>}
                    </div>)}
                </div>
              </div>
              {profile.projects.length > 0 && <div data-id="element-515">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-516">
                    Projects
                  </label>
                  <div className="space-y-3" data-id="element-517">
                    {profile.projects.map((project, index) => <div key={index} className="p-3 bg-gray-50 rounded-lg" data-id="element-518">
                        <div className="flex items-center gap-2 mb-1" data-id="element-519">
                          <FolderIcon className="h-4 w-4 text-gray-400" data-id="element-520" />
                          <span className="font-medium text-gray-900" data-id="element-521">
                            {project.title}
                          </span>
                        </div>
                        <a href={project.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 flex items-center" data-id="element-522">
                          <LinkIcon className="h-3 w-3 mr-1" data-id="element-523" />
                          {project.url}
                        </a>
                        {project.description && <p className="text-sm text-gray-600 mt-1" data-id="element-524">
                            {project.description}
                          </p>}
                      </div>)}
                  </div>
                </div>}
              <div data-id="element-525">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-526">
                  Open To
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-527">
                  {profile.openTo.map(option => <span key={option} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm" data-id="element-528">
                      {option}
                    </span>)}
                </div>
              </div>
              {(profile.website || profile.github || profile.twitter || profile.linkedin) && <div data-id="element-529">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-530">
                    Links
                  </label>
                  <div className="space-y-2" data-id="element-531">
                    {profile.website && <a href={profile.website} target="_blank" rel="noopener noreferrer" className="flex items-center text-[#FF3B30] hover:text-[#E02E23]" data-id="element-532">
                        <GlobeIcon className="h-4 w-4 mr-2" data-id="element-533" />
                        Website
                      </a>}
                    {profile.github && <a href={profile.github} target="_blank" rel="noopener noreferrer" className="flex items-center text-[#FF3B30] hover:text-[#E02E23]" data-id="element-534">
                        <GithubIcon className="h-4 w-4 mr-2" data-id="element-535" />
                        GitHub
                      </a>}
                    {profile.twitter && <a href={profile.twitter} target="_blank" rel="noopener noreferrer" className="flex items-center text-[#FF3B30] hover:text-[#E02E23]" data-id="element-536">
                        <TwitterIcon className="h-4 w-4 mr-2" data-id="element-537" />
                        Twitter
                      </a>}
                    {profile.linkedin && <a href={profile.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center text-[#FF3B30] hover:text-[#E02E23]" data-id="element-538">
                        <LinkedinIcon className="h-4 w-4 mr-2" data-id="element-539" />
                        LinkedIn
                      </a>}
                  </div>
                </div>}
            </div> : <div className="text-center py-12" data-id="element-540">
              <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-541" />
              <p className="text-gray-600" data-id="element-542">No profile created yet</p>
            </div>}
        </Card>
      </div>
    </div>;
};
export default DeveloperDashboard;